import { useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Topbar } from "./Topbar";
import { Statusbar } from "./Statusbar";
import { Rail } from "./Rail";
import { HubSubNav, HUB_SUBNAV } from "./HubSubNav";
import { SHELL_APPS, type ShellApp } from "../../lib/apps";
import { useAllowedApps } from "../../hooks/useAllowedApps";
import { useAuth } from "../../hooks/useAuth";
import { useManifestSwitch } from "../../hooks/useManifestSwitch";
import { isGfRole } from "../../../../shared/permissions";
import { INSTANCE } from "../../lib/instance";
import { isCoreInstance } from "../../../../shared/instance";

const BREADCRUMB_MAP: Record<string, string> = {
  home:        "Home · App-Launcher",
  hub:         "Hub",
  notes:       "Nexus",
  nexus:       "Nexus",
  liquid:      "Liquid · Dashboard",
  briefing:    "Briefing",
  life:        "Life",
  fair:        "FAIR-Kapa · Dashboard",

  regelwerke:  "Regelwerke",
  time:        "Time",
  admin:       "Administration",
  ds:          "Design System",
  settings:    "Einstellungen",
};

function resolveActiveApp(pathname: string): string {
  if (pathname === "/home") return "home";
  if (pathname.startsWith("/design-system")) return "ds";
  if (pathname === "/hub/einstellungen" || pathname.startsWith("/hub/einstellungen/")) return "settings";
  if (pathname === "/admin" || pathname.startsWith("/admin/")) return "admin";
  if (pathname === "/hub" || pathname.startsWith("/hub/")) return "hub";

  for (const app of SHELL_APPS) {
    if (app.id === "hub") continue;
    if (pathname === app.route || pathname.startsWith(app.route + "/")) {
      return app.id;
    }
  }
  return "hub";
}

const MOBILE_LAST_APP_KEY = "hub:lastApp:mobile";
const MOBILE_BREAKPOINT = 768;

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { allowed } = useAllowedApps();

  const activeApp = resolveActiveApp(location.pathname);
  const breadcrumb = BREADCRUMB_MAP[activeApp] ?? "";

  // PWA-Manifest dynamisch umschalten: Wenn eine Satellite-App aktiv ist,
  // wird <link rel="manifest"> auf das Satellite-Manifest gesetzt, damit
  // Chrome die korrekte App zur Installation anbietet (nicht immer "Hub").
  useManifestSwitch(activeApp);

  // Mobile: aktuelle App-Route in localStorage speichern für Wiederherstellung
  useEffect(() => {
    if (window.innerWidth > MOBILE_BREAKPOINT) return;
    const app = SHELL_APPS.find((a) => a.id === activeApp);
    if (app && app.kind !== "hub" && app.id !== "settings" && app.id !== "admin") {
      try { localStorage.setItem(MOBILE_LAST_APP_KEY, app.route); } catch { /* ignore */ }
    }
  }, [activeApp]);

  const isGf = isCoreInstance(INSTANCE)
    ? user?.coreAdministrator === true
    : isGfRole(user?.roleName, INSTANCE, user?.roleScope);

  // RBAC-gefilterte App-Liste. Die Registry-Reihenfolge ist zugleich die
  // verbindliche Rail- und Home-Kreis-Reihenfolge.
  const visibleApps: ShellApp[] = useMemo(() => {
    return SHELL_APPS.filter((app) => {
      if (app.appKey == null) return true;
      if (allowed == null) return false;
      return allowed.has(app.appKey);
    });
  }, [allowed]);

  const shortcutApps = useMemo(
    () => visibleApps.filter((app) => app.id !== "hub" && app.showInRail !== false),
    [visibleApps],
  );

  // Tastatur-Shortcuts: 1..7 wechseln zur n-ten sichtbaren Rail-App.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const idx = parseInt(e.key, 10);
      if (idx >= 1 && idx <= shortcutApps.length) {
        const item = shortcutApps[idx - 1];
        if (item.kind === "external" && item.externalUrl) {
          window.open(item.externalUrl, "_blank", "noopener");
        } else {
          navigate(item.route);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate, shortcutApps]);

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : user?.username?.[0]?.toUpperCase() ?? "?";

  const isHomePage = location.pathname === "/home";

  // Stammdaten liegen seit der Bereinigung unter Administration > Daten.
  // Die frühere Reiterleiste ist daher bewusst leer; die Komponente bleibt
  // als stabiles Shell-Element für eventuelle rein persönliche Hub-Navigation.
  const showHubSubNav = HUB_SUBNAV.length > 0 && (isHomePage || activeApp === "hub" || activeApp === "settings");
  const visibleSubNav = HUB_SUBNAV;

  // Padding nur fuer Hub-eigene Pages — Satellite-Iframes fuellen den Slot komplett.
  // Admin braucht ein eigenes Layout ohne max-w Wrapper.
  const isHubPage = activeApp === "hub" || activeApp === "settings" || activeApp === "ds";
  const isOwnLayoutPage = activeApp === "admin" || isHomePage;
  const isSatelliteSlot = !isHubPage && !isOwnLayoutPage;

  // Alle Administrationseinträge ausser der separaten Stammdaten-Ausnahme
  // sind GF-only. Die historische isAdmin-Markierung darf keine zusätzlichen
  // Fach- oder Verwaltungsansichten freischalten.
  const showAdministration = isGf;

  return (
    <div className={`os-shell${isHomePage ? " os-shell--home" : ""}`} data-app={activeApp}>
      <Topbar
        appId={activeApp}
        breadcrumb={breadcrumb}
        initials={initials}
        showAdministration={showAdministration}
      />

      {/* Home zeigt den grossen Kreis; nach App-Auswahl uebernimmt die Rail. */}
      {!isHomePage ? (
        <Rail
          activeId={activeApp}
          visibleApps={visibleApps}
          showAdministration={showAdministration}
        />
      ) : null}

      <main
        className="os-main"
        style={
          (isSatelliteSlot || isOwnLayoutPage)
            ? { overflow: "hidden" }
            : undefined
        }
      >
        {showHubSubNav ? <HubSubNav items={visibleSubNav} isGf={isGf} /> : null}
        {isSatelliteSlot ? (
          <Outlet />
        ) : isOwnLayoutPage ? (
          <Outlet />
        ) : (
          <div className="p-4 md:p-8 w-full max-w-[1200px]">
            <Outlet />
          </div>
        )}
      </main>
      <Statusbar appId={activeApp} breadcrumb={breadcrumb} />
    </div>
  );
}
